import React, { useEffect, useState } from "react";
import { Box, Link, TextField, Typography } from "@mui/material";

import CriteriaTextFieldComponent from "./TextFields/CriteriaTextFieldComponent";
import PolicyTranslationUserSelect from "./Selects/PolicyTranslationUserSelect";
import api from "../../api";
import { useDispatch } from "react-redux";
import { setDialog, setLoading } from "slices/appSlice";
import {
  POET_AVALON_POE,
  USERGROUP_TRANSLATION,
  USERGROUP_TRANSLATION_REVIEW,
} from "constants/Constants";
import PolicyTypeBanner from "components/PolicyTypeBanner";
import FirstLineContainer from "components/common/FirstLineContainer";
import Drawers from "../../components/common/Drawers";
import GreyOutButton from "components/GreyOutButton/GreyOutButton";
import ViewClobButton from "utils/ViewClobButton";

const TopPanelComponent = ({
  policyStateData,
  healthPlanDisplayName,
  policyNumber,
  policyTitle,
  editable,
  setEndDateOpen,
  startedFromScratch,
  healthPlanKey,
  policyKey,
  workspaceController,
  handleCancelAction,
  policyVersion,
  children,
  policyData,
  setPolicyData,
  ctReviewData,
  policyHistoryData,
  setPolicyHistoryData,
  reviewData,
  setReviewData,
  resetFilter,
}) => {
  const [translationReviewers, setTranslationReviewers] = useState([]);
  const [policyTranslators, setPolicyTranslators] = useState([]);
  const [changeControlText, setChangeControlText] = useState("");
  const [activeDrawer, setActiveDrawer] = useState(null);

  const dispatch = useDispatch();

  useEffect(() => {
    setChangeControlText(
      policyData.policyChangeControlXrefDto?.changeControlText || ""
    );
  }, [policyData.policyChangeControlXrefDto?.changeControlText]);

  //Populate drop downs
  useEffect(() => {
    const fetchUsers = async () => {
      dispatch(setLoading(true));

      try {
        // Fetch the list of users for the dropdowns
        const usersResponse = await api.healthPlanConfig.fetchAllUsers();

        setPolicyTranslators(
          usersResponse.data.filter((user) => {
            let returnUser = false;

            if (
              user.userGroupName === POET_AVALON_POE &&
              user.poetUserAttributes.includes(USERGROUP_TRANSLATION)
            ) {
              returnUser = true;
            }

            return returnUser;
          })
        );

        setTranslationReviewers(
          usersResponse.data.filter((user) => {
            let returnUser = false;

            if (
              user.poetUserAttributes.includes(USERGROUP_TRANSLATION_REVIEW)
            ) {
              returnUser = true;
            }

            return returnUser;
          })
        );
      } catch (e) {
        dispatch(
          setDialog({
            message: e.message,
            title: "Error: Could not fetch users",
          })
        );
      } finally {
        dispatch(setLoading(false));
      }
    };
    fetchUsers();
  }, [dispatch]);

  useEffect(() => {
    setChangeControlText(
      policyData.policyChangeControlXrefDto?.changeControlText || ""
    );
  }, [policyData.policyChangeControlXrefDto?.changeControlText]);

  const handleDrawerButtonClick = (drawerType) => {
    setActiveDrawer(drawerType);
  };
  const handleTranslatorChange = (event) => {
    setPolicyData((prev) => ({
      ...prev,
      policyTranslator: event.target.value,
    }));
  };
  const handleReviewerChange = (event) => {
    setPolicyData((prev) => ({
      ...prev,
      translationReviewer: event.target.value,
    }));
  };

  const closeDrawer = () => {
    setActiveDrawer(null);
  };

  const groupBoxSx = {
    sx: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: 1,
    },
  };

  const typographyStyle = {
    component: "span",
    fontWeight: "bold",
    whiteSpace: "nowrap",
    sx: { flex: 0.5 },
  };

  const disableAdoptionDetailsDrawer = () => {
    return policyData.overallAdoptionDecision === null;
  };

  const handleChangeControlChange = (event) => {
    setPolicyData((prevData) => ({
      ...prevData,
      policyChangeControlXrefDto: {
        ...prevData.policyChangeControlXrefDto,
        changeControlText: event.target.value,
      },
    }));
  };

  const policyTypeBannerText =
    `${healthPlanDisplayName} - ${policyVersion}` +
    (policyData.overallAdoptionDecision
      ? ` - ${policyData.overallAdoptionDecision}`
      : "");

  return (
    <>
      <FirstLineContainer>
        <Typography variant="h6" sx={{ textAlign: "left", flex: 1 }}>
          <Box component="span" fontWeight="fontWeightBold">
            Policy Rules:
          </Box>{" "}
          {policyNumber} - {policyTitle}
        </Typography>
        <Typography variant="h6" sx={{ textAlign: "right", flex: 1 }}>
          {workspaceController?.getPolicyHeader(0)?.PolicyType || ""}
        </Typography>
        <ViewClobButton data-testid="policy-translation-view-clob-button" />
      </FirstLineContainer>
      <PolicyTypeBanner
        bannerText={policyTypeBannerText}
        handleCancel={handleCancelAction}
        handleDraft={() => resetFilter(false)}
        handleReview={() => resetFilter(true)}
        editable={editable}
      />
      <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
        {/* Left Column */}
        <Box
          sx={{
            flex: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            mr: 10,
          }}
        >
          <Box {...groupBoxSx}>
            <Typography {...typographyStyle}>Review Type:</Typography>
            <Typography sx={{ flex: 1 }} component="span">
              {policyStateData?.cowName} {policyStateData?.quarter}
            </Typography>
          </Box>
          <Box {...groupBoxSx}>
            <Typography
              sx={{ flex: 0.5 }}
              component="span"
              fontWeight="bold"
              whiteSpace="nowrap"
            >
              Change Control:
            </Typography>
            <TextField
              variant="outlined"
              fullWidth
              sx={{ flex: 1 }}
              value={changeControlText}
              onChange={(e) => setChangeControlText(e.target.value?.trim())}
              onBlur={(e) => handleChangeControlChange(e)}
            />
          </Box>
          <Box {...groupBoxSx}>
            <Typography
              sx={{ flex: 0.5 }}
              component="span"
              fontWeight="bold"
              whiteSpace="nowrap"
            >
              Total Coverage Criteria:
            </Typography>
            <CriteriaTextFieldComponent
              disabled={false}
              sx={{ flex: 1 }}
              totalCoverageCriteria={
                workspaceController?.getRuleHeader(0)?.TotalCoverageCriteria ||
                ""
              }
            />
          </Box>
        </Box>
        {/* Center Column */}
        <Box
          sx={{
            flex: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            mr: 10,
          }}
        >
          <Box {...groupBoxSx}>
            <Typography {...typographyStyle}>Translation Status:</Typography>
            <Typography sx={{ flex: 1 }} component="span">
              {policyData.translationStatus}
            </Typography>
          </Box>
          <Box {...groupBoxSx}>
            <Typography {...typographyStyle}>Policy Translator:</Typography>
            <PolicyTranslationUserSelect
              sx={{ flex: 1 }}
              users={policyTranslators}
              selectedUser={policyData.policyTranslator}
              setSelectedUser={handleTranslatorChange}
            ></PolicyTranslationUserSelect>
          </Box>
          <Box {...groupBoxSx}>
            <Typography {...typographyStyle}>Translation Reviewer:</Typography>
            <PolicyTranslationUserSelect
              sx={{ flex: 1 }}
              users={translationReviewers}
              selectedUser={policyData.translationReviewer}
              setSelectedUser={handleReviewerChange}
            ></PolicyTranslationUserSelect>
          </Box>
        </Box>

        {/* Right Column */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 0.1,
            ml: 10,
            mb: 2,
            alignItems: "flex-end",
            textAlign: "right",
          }}
        >
          {editable && !startedFromScratch && (
            <Box display="flex" flexDirection="column">
              <Link
                component="button"
                onClick={() => setEndDateOpen(true)}
                underline="hover"
                color="#FC3E30"
                align="right"
                fontWeight="fontWeightBold"
              >
                START FROM SCRATCH
              </Link>
            </Box>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <GreyOutButton
              variant="outlined"
              size="small"
              disabled={disableAdoptionDetailsDrawer()}
              onClick={() => handleDrawerButtonClick("adoptionDetails")}
            >
              Adoption Details
            </GreyOutButton>
            <GreyOutButton
              variant="outlined"
              size="small"
              disabled={
                policyData.translationStatus === "In Progress" ||
                policyData.translationStatus === "Assigned"
              }
              onClick={() => handleDrawerButtonClick("ctReviews")}
            >
              C&T Reviews
            </GreyOutButton>
            <GreyOutButton
              variant="outlined"
              size="small"
              onClick={() => handleDrawerButtonClick("policyHistory")}
            >
              Policy History
            </GreyOutButton>
          </Box>
        </Box>
      </Box>
      {children}
      {/* Use the Drawers component */}
      {activeDrawer && (
        <Drawers
          policyVersion={policyVersion}
          editable={editable}
          activeDrawer={activeDrawer}
          closeDrawer={closeDrawer}
          healthPlanKey={healthPlanKey}
          policyKey={policyKey}
          status={policyData.translationStatus}
          lobAdoptionDecisions={policyData.lobAdoptionDecisionList}
          policyHistoryData={policyHistoryData}
          setPolicyHistoryData={setPolicyHistoryData}
          ctReviewData={ctReviewData}
          reviewData={reviewData}
          setReviewData={setReviewData}
        />
      )}
    </>
  );
};

export default TopPanelComponent;
